import envConfig from "../../../utils/env.config";

const BUILD_RESTAURANT_ID_KEY = "prepit_build_restaurant_id";
const PERSIST_KEY_PREFIX = "persist:";

const safeJsonParse = (value: string | null) => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

// When the stamp is missing (state written before this guard shipped) fall
// back to inspecting the persisted config slice: a restaurantInfo.id that
// differs from the build id is stale, unless the build restaurant is in the
// persisted partner list (i.e. the user legitimately switched to a partner
// restaurant within this deployment).
const isLegacyStateStale = (buildRestaurantId: number) => {
  const persistedConfig = safeJsonParse(
    localStorage.getItem(`${PERSIST_KEY_PREFIX}config`)
  );
  if (!persistedConfig) {
    return false;
  }
  const persistedRestaurantId = safeJsonParse(
    persistedConfig.restaurantInfo
  )?.id;
  if (!persistedRestaurantId || persistedRestaurantId === buildRestaurantId) {
    return false;
  }
  const partnerRestaurants: Array<{ id?: number }> =
    safeJsonParse(persistedConfig.partnerRestaurants) ?? [];

  return !partnerRestaurants.some(
    (partner) => partner?.id === buildRestaurantId
  );
};

/**
 * Domains can be re-pointed to a different restaurant across deployments
 * (e.g. maine.prepit.app moving from restaurant 21 to 876). redux-persist
 * would then keep serving the old restaurant/franchise ids from localStorage,
 * breaking every returning visitor. Drop all persisted state when the build's
 * restaurant id no longer matches the one the state was written for.
 *
 * Must run in the browser before persistStore() triggers rehydration.
 */
export const purgeStaleRestaurantPersistedState = () => {
  if (typeof window === "undefined" || !envConfig.restaurantId) {
    return;
  }
  try {
    const stampedBuildId = localStorage.getItem(BUILD_RESTAURANT_ID_KEY);
    const isStale = stampedBuildId
      ? stampedBuildId !== envConfig.restaurantId
      : isLegacyStateStale(parseInt(envConfig.restaurantId));

    if (isStale) {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(PERSIST_KEY_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    }
    localStorage.setItem(BUILD_RESTAURANT_ID_KEY, envConfig.restaurantId);
  } catch {
    // Storage unavailable (private mode quirks, disabled storage) - never
    // block app boot for the purge.
  }
};
